/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  ColDef,
  ExcelExportParams,
  ExcelStyle,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from '@ag-grid-community/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { combineQueries } from '@datorama/akita';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import first from 'lodash-es/first';
import { BehaviorSubject, combineLatest, of, ReplaySubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ToggleCurrencyComponent } from '@widgets/toggle-currency/toggle-currency.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TimelineService } from '@models/milestone-category/milestone/timeline.service';
import { PatientProtocolQuery } from '@models/patient-protocol/patient-protocol.query';
import { PatientProtocolService } from '@models/patient-protocol/patient-protocol.service';
import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';

import { IconComponent } from '@shared/components/icon/icon.component';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import {
  Currency,
  listSitePatientTrackerVersionsQuery,
  PatientProtocolType,
} from '@shared/services/gql.service';
import { TableService } from '@shared/services/table.service';
import { MainQuery } from '@shared/store/main/main.query';
import { PaymentSchedulesQuery } from '@shared/store/payment-schedules/payment-schedules.query';
import { PaymentSchedulesModel } from '@shared/store/payment-schedules/payment-schedules.store';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { CurrencyToggle } from '@shared/types/components.type';
import {
  AgSetColumnsVisible,
  AuxExcelFormats,
  AuxExcelStyleKeys,
  AuxExcelStyles,
} from '@shared/utils';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    NgSelectComponent,
    FormsModule,
    ToggleComponent,
    ToggleCurrencyComponent,
    NgOptionComponent,
    ExportExcelButtonComponent,
    AgGridAngular,
    RouterLink,
    AsyncPipe,
    DatePipe,
  ],
  selector: 'aux-investigator-detail',
  styles: [],
  templateUrl: './investigator-detail.component.html',
})
export class InvestigatorDetailComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private mainQuery = inject(MainQuery);
  private patientProtocolQuery = inject(PatientProtocolQuery);
  private patientProtocolService = inject(PatientProtocolService);
  private paymentSchedulesQuery = inject(PaymentSchedulesQuery);
  private sitesQuery = inject(SitesQuery);
  private sitesService = inject(SitesService);
  private timelineQuery = inject(TimelineQuery);
  private timelineService = inject(TimelineService);
  private workflowService = inject(WorkflowService);

  loading$ = combineLatest([
    this.sitesQuery.selectLoading(),
    this.timelineQuery.selectLoading(),
    this.patientProtocolQuery.selectLoading(),
  ]).pipe(map((arr) => arr.some((x) => x)));

  currencyToggle = CurrencyToggle;
  currentOpenMonth = '';
  selectedVisibleCurrency$ = new BehaviorSubject<CurrencyToggle>(CurrencyToggle.PRIMARY);
  defaultColumns: ColDef[] = [
    {
      colId: 'site_id',
      field: 'site_id',
      headerName: 'SITE ID',
      hide: true,
    },
    {
      field: 'site_no',
      headerName: 'Site No',
      minWidth: 150,
      pinned: 'left',
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'patient',
      headerName: 'Total Visit Costs',
      valueFormatter: Utils.agMultipleCurrencyFormatter(this.selectedVisibleCurrency$),
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'other',
      headerName: 'Total Invoiceables',
      valueFormatter: Utils.agMultipleCurrencyFormatter(this.selectedVisibleCurrency$),
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'total_month',
      headerName: 'Total Month Costs',
      pinned: 'right',
      valueFormatter: Utils.agMultipleCurrencyFormatter(this.selectedVisibleCurrency$),
    },
    {
      cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'total_all',
      headerName: 'Total Costs to Date',
      minWidth: 150,
      pinned: 'right',
      valueFormatter: Utils.agMultipleCurrencyFormatter(this.selectedVisibleCurrency$),
    },
  ];
  display$ = new BehaviorSubject<'costs' | 'count'>('count');
  // Static ExcelStyles for this grid
  excelStyles: ExcelStyle[] = [
    {
      dataType: 'Number',
      id: 'site_no',
    },
    {
      dataType: 'Number',
      id: 'other',
      numberFormat: { format: AuxExcelFormats.Cost },
    },
    {
      dataType: 'Number',
      id: 'total_month',
      numberFormat: { format: AuxExcelFormats.Cost },
    },
    {
      dataType: 'Number',
      id: 'total_all',
      numberFormat: { format: AuxExcelFormats.Cost },
    },
  ];
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$ = new BehaviorSubject<Record<string, unknown>[]>([]);
  gridOptions: ExcelExportParams = {
    fileName: 'auxilius-investigator-detail.xlsx',
    sheetName: 'Investigator Detail',
  };
  gridOptions$ = new BehaviorSubject<GridOptions>({
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      minWidth: 100,
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnTypes: Utils.columnTypes,
    excelStyles: [],
    suppressMenuHide: true,
  });
  isContractedCurrency = false;
  isDisplayCosts = false;
  lastSourceDataRefreshDate = '';
  monthList: { label: string; value: string }[] = [];
  patientProtocolExcel: ExcelStyle[] = [];
  patientTrackerLink = `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PATIENT_TRACKER}`;
  selectedMonth$ = new BehaviorSubject(this.currentOpenMonth);
  selectedMonthFC = this.currentOpenMonth;
  selectedVersion: string | undefined = '';
  showNoData$ = new BehaviorSubject(false);
  versions$ = new BehaviorSubject<listSitePatientTrackerVersionsQuery[]>([]);

  private aggregateColumns = ['patient', 'other', 'overhead', 'total_month', 'total_all'];

  ngOnInit() {
    this.mainQuery
      .select('trialKey')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          this.sitesService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
          this.initMonthList();
          this.initSelectedMonth();
          this.initPatientProtocols();

          return of(true);
        })
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          return this.getGridData$();
        })
      )
      .subscribe((rows) => {
        this.gridData$.next(rows);
        this.gridAPI.sizeColumnsToFit();
      });

    this.gridAPI$
      .pipe(
        switchMap(() => combineLatest([this.gridData$, this.selectedVisibleCurrency$])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([rows, selectedVisibleCurrency]) => {
        if (selectedVisibleCurrency !== this.currencyToggle.CONTRACTED) {
          this.generatePinnedBottomData(rows);
        } else {
          this.gridAPI.setGridOption('pinnedBottomRowData', []);
        }
      });

    this.gridAPI$
      .pipe(
        switchMap(() => this.display$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        const { columnDefs } = this.gridOptions$.getValue();

        const colIds: string[] = [];

        columnDefs?.forEach((x) => {
          if ('field' in x && x.field?.includes('::')) {
            colIds.push(x.field);
          }
        });

        const countColIds = colIds.filter((x) => x.includes('::count'));
        const costsColIds = colIds.filter((x) => x.includes('::costs'));

        AgSetColumnsVisible({
          gridApi: this.gridAPI,
          keys: countColIds,
          visible: value === 'count',
        });

        AgSetColumnsVisible({
          gridApi: this.gridAPI,
          keys: costsColIds,
          visible: value === 'costs',
        });

        this.sizeColumnsToFit();
      });
  }

  private generatePinnedBottomData(rows: Record<string, unknown>[]) {
    const data = rows.reduce(
      (acc: Record<string, number>, val) => {
        Object.entries(val).forEach(([column, value]) => {
          if (typeof value === 'number') {
            acc[column] ||= 0;
            acc[column] += value as number;
          }
        });

        return acc;
      },
      {
        other: 0,
        overhead: 0,
        patient: 0,
        total_all: 0,
        total_month: 0,
      }
    );

    this.gridAPI?.setGridOption('pinnedBottomRowData', [
      { ...data, currency: Currency.USD, site_no: 'Total' },
    ]);
  }

  getDynamicExcelParams = (): ExcelExportParams => {
    const name = this.mainQuery.getSelectedTrial()?.short_name;
    const totals = this.gridAPI.getPinnedBottomRow(0)?.data;
    const columns = totals
      ? (Object.entries(totals)
          .map(([key, value]) => (typeof value === 'number' ? key : null))
          .filter((key) => key) as string[])
      : this.aggregateColumns;
    const selectedVersionDate = this.versions$
      .getValue()
      .find((version) => version.id === this.selectedVersion)?.create_date;

    const appendContent: ExcelExportParams['appendContent'] = totals
      ? [
          {
            cells: [
              {
                data: { type: 'String', value: `Total` },
                styleId: 'total_row_header',
              },
              ...TableService.getTotalRowForExcel({
                aggregateColumns: columns,
                excludeColumns: ['site_no', 'currency'],
                gridApi: this.gridAPI,
                totalData: totals,
              }),
            ],
          },
        ]
      : [{ cells: [] }];

    return {
      appendContent,
      prependContent: [
        {
          cells: [
            {
              data: {
                type: 'String',

                value: `Trial: ${name} - Version: ${new DatePipe('en-US').transform(
                  selectedVersionDate || '',
                  'MM.dd.YYYY HH:mm'
                )}`,
              },
              mergeAcross: 3,
              styleId: 'first_row',
            },
          ],
        },
      ],
      processCellCallback: TableService.processCellForExcel(
        this.selectedVisibleCurrency$,
        this.display$.getValue() === 'costs' ? columns : this.aggregateColumns,
        '::costs'
      ),
      shouldRowBeSkipped(params) {
        return params.node?.data?.site_no === 'Total';
      },
    };
  };

  // eslint-disable-next-line max-lines-per-function
  private getGridData$() {
    return combineLatest([this.selectedVisibleCurrency$]).pipe(
      // eslint-disable-next-line max-lines-per-function
      switchMap(([selectedVisibleCurrency]) => {
        return combineQueries([
          this.paymentSchedulesQuery.selectAll({ asObject: true }),
          this.gridAPI$,
        ]).pipe(
          // eslint-disable-next-line max-lines-per-function
          map(() => {
            AgSetColumnsVisible({
              gridApi: this.gridAPI,
              keys: ['total_month'],
              visible: this.selectedMonth$.getValue() !== 'ALL',
            });

            const gridData: Record<string, unknown>[] = [];

            const siteObj: Record<string, PaymentSchedulesModel[]> = {};
            const siteSet: Record<
              string,
              {
                sps_total_contract_expense_amount?: number;
                sps_total_expense_amount?: number;
              }
            > = {};

            Object.entries(siteObj).forEach(([site_id, paymentSchedulesGroupedBySite]) => {
              gridData.push({
                site_id,
                site_no: this.sitesQuery.getEntity(site_id)?.site_no,
                ...paymentSchedulesGroupedBySite.reduce(
                  (acc, val) => {
                    const amountKey =
                      selectedVisibleCurrency === this.currencyToggle.PRIMARY
                        ? 'amount'
                        : 'amount_contract';

                    acc.contractCurrency = val.sps_contract_expense_currency || Currency.USD;
                    acc.currency = val.sps_expense_currency || Currency.USD;

                    if (
                      this.selectedMonth$.getValue() !== 'ALL' &&
                      this.selectedMonth$.getValue().slice(0, 7) !==
                        val.completion_date?.slice(0, 7)
                    ) {
                      return acc;
                    }

                    acc.total_month += val[amountKey] || 0;

                    // for dynamic columns
                    acc[`${val.patient_protocol_id}::costs`] ||= 0;
                    acc[`${val.patient_protocol_id}::count`] ||= 0;
                    // for dynamic columns
                    acc[`${val.patient_protocol_id}::costs`] =
                      (acc[`${val.patient_protocol_id}::costs`] as number) + (val[amountKey] || 0);
                    acc[`${val.patient_protocol_id}::count`] =
                      (acc[`${val.patient_protocol_id}::count`] as number) + 1;

                    switch (val.patient_protocol_type) {
                      case PatientProtocolType.PATIENT_PROTOCOL_DISCONTINUED:
                      case PatientProtocolType.PATIENT_PROTOCOL_OTHER:
                      case PatientProtocolType.PATIENT_PROTOCOL_SCREEN_FAIL:
                        acc.other += val[amountKey] || 0;
                        break;
                      case PatientProtocolType.PATIENT_PROTOCOL_OVERHEAD:
                        acc.other += val[amountKey] || 0;
                        break;
                      case PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT:
                        acc.patient += val[amountKey] || 0;
                        break;
                      default:
                        break;
                    }

                    return acc;
                  },
                  {
                    contractCurrency: '',
                    currency: '',
                    other: 0,
                    overhead: 0,
                    patient: 0,
                    total_month: 0,
                  } as {
                    [k: string]: number | string;
                    other: number;
                    overhead: number;
                    patient: number;
                    total_month: number;
                  }
                ),
                total_all:
                  selectedVisibleCurrency === this.currencyToggle.PRIMARY
                    ? siteSet[site_id].sps_total_expense_amount
                    : siteSet[site_id].sps_total_contract_expense_amount,
              });
            });

            this.showNoData$.next(false);

            return gridData;
          })
        );
      })
    );
  }

  getListSitePatientTrackerVersions(): void {
    this.sitesService
      .listSitePatientTrackerVersions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value.success && value.data) {
          const currentVersion = value.data.find((item) => item.is_current);

          this.versions$.next(value.data);
          this.selectedVersion = currentVersion?.id || '';
          this.lastSourceDataRefreshDate = currentVersion?.create_date || '';
        }
      });
  }

  private initGridOptions(): void {
    const patientProtocols = this.patientProtocolQuery.getAll();
    const patientProtocolColumns: ColDef[] = [];

    patientProtocols.forEach((patientProtocol) => {
      if (
        [
          PatientProtocolType.PATIENT_PROTOCOL_DISCONTINUED,
          PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT,
          PatientProtocolType.PATIENT_PROTOCOL_SCREEN_FAIL,
        ].includes(patientProtocol.patient_protocol_type)
      ) {
        patientProtocolColumns.push({
          ...TableConstants.dynamicColumnProps(patientProtocol.name),
          field: `${patientProtocol.id}::count`,
          valueFormatter: (params) => params.value || Utils.zeroHyphen,
        });
        this.patientProtocolExcel.push({
          dataType: 'Number',
          id: patientProtocol.name,
          numberFormat: { format: AuxExcelFormats.Cost },
        });
        patientProtocolColumns.push({
          ...TableConstants.dynamicColumnProps(patientProtocol.name),
          cellClass: [AuxExcelStyleKeys.CELL_RIGHT, TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
          field: `${patientProtocol.id}::costs`,
          valueFormatter: Utils.agMultipleCurrencyFormatter(this.selectedVisibleCurrency$),
        });
      }
    });

    this.gridOptions$.next({
      ...this.gridOptions$.getValue(),
      columnDefs: [
        ...this.defaultColumns.slice(0, 2),
        ...patientProtocolColumns,
        ...this.defaultColumns.slice(2),
      ],
      excelStyles: [...AuxExcelStyles],
    });

    this.display$.next(this.display$.getValue());
    this.getListSitePatientTrackerVersions();
  }

  private initMonthList() {
    this.timelineService
      .getTimelineItems()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          const { items } = this.timelineQuery.getValue();
          const start_date = first(items)?.contract_start_date;
          const month_list: { label: string; value: string }[] = [];

          if (start_date) {
            let date = Utils.dateParse(`${start_date.slice(0, 8)}01`);

            while (date < new Date()) {
              const value = date.toISOString().slice(0, 10);

              month_list.push({
                label: Utils.dateFormatter(value, { day: undefined, year: '2-digit' }),
                value,
              });
              date = Utils.addMonths(date, 1);
            }
          }

          this.monthList = month_list.slice(0).reverse();
        })
      )
      .subscribe();
  }

  private initPatientProtocols(): void {
    this.patientProtocolService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initGridOptions();
      });
  }

  private initSelectedMonth() {
    const currentOpenMonth = this.workflowService.getTrialInformation();

    if (currentOpenMonth) {
      this.selectedMonthFC = dayjs(currentOpenMonth).endOf('month').format('YYYY-MM-DD');
    } else {
      this.selectedMonthFC = dayjs(new Date().toISOString().slice(0, 7))
        .endOf('month')
        .format('YYYY-MM-DD');
    }

    this.selectedMonth$.next(this.selectedMonthFC);
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.gridAPI$.next(api);
    Utils.updateGridLayout(this.gridAPI, 'investigatorDetailsGrid', true);
  }

  onVersionChange(): void {
    this.isContractedCurrency =
      this.selectedVisibleCurrency$.getValue() !== this.currencyToggle.PRIMARY;
    this.isDisplayCosts = this.display$.getValue() === 'costs';
  }

  sizeColumnsToFit(): void {
    this.gridAPI.sizeColumnsToFit();
  }
}
